// src/components/ClientDocuments.jsx
// Console → Clients → a client's details → Documents (3.36.0, Super Admins):
// the client's signed contracts (Service Agreement, BAA, NDA, others).
// Files are in the client's own folder in Google Cloud Storage (named after
// the client), under the names they were uploaded with; files put straight
// into that folder show here too, to be described. Rename changes the name in
// Cloud Storage as well. Nothing is deleted automatically; Delete (3.36.1,
// with a confirmation) removes a file from the portal and the folder. A
// renewal date shows on the Overview 30 days before.
// 3.68.0 (owner): View opens the document inside the portal (DocumentViewer),
// from our own backend, not a storage.googleapis.com link.
import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useNotify } from './Notifications';
import { apiCall } from './EmailEditorParts';
import { PILL } from '../theme/tabs';
import Loader from './Loader';
import Emo from './Emo';
import DocumentViewer from './DocumentViewer';
import { API_BASE } from '../firebase/config';

const ACCEPT = '.pdf,.doc,.docx,.png,.jpg,.jpeg,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/png,image/jpeg';
const today = () => new Date().toISOString().slice(0, 10);
const soon = d => d && d <= new Date(Date.now() + 30 * 86400e3).toISOString().slice(0, 10);
const fmtDay = d => (d ? new Date(`${d}T12:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—');
const kb = n => (n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);
const EMPTY = { type: 'service_agreement', label: '', signedOn: '', expiresOn: '', notes: '', replaces: '' };

export default function ClientDocuments({ account }) {
  const { getToken } = useAuth();
  const { notify, confirm } = useNotify();
  const [data, setData] = useState(null);       // { folder, bucket, data, types }
  const [error, setError] = useState('');
  const [form, setForm] = useState(null);       // { mode: 'upload'|'describe', path?, file?, ...fields }
  const [busy, setBusy] = useState('');
  const [renaming, setRenaming] = useState(null);   // { path, name }
  const [showOld, setShowOld] = useState(false);
  const [viewing, setViewing] = useState(null);   // the document open in the viewer
  const base = `/portal/accounts/${encodeURIComponent(account.id)}/documents`;

  const load = useCallback(async () => {
    setError('');
    try { setData(await apiCall(base, getToken)); } catch (e) { setError(e.message); }
  }, [base, getToken]);
  useEffect(() => { load(); }, [load]);

  const types = data?.types || {};
  const docs = data?.data || [];
  const current = docs.filter(d => d.status !== 'replaced');
  const older = docs.filter(d => d.status === 'replaced');

  const fileUrl = d => `${API_BASE}${base}/file?path=${encodeURIComponent(d.path)}`;
  async function open(d, download) {
    if (!download) { setViewing(d); return; }
    try {
      const r = await fetch(`${fileUrl(d)}&download=1`, { headers: { Authorization: `Bearer ${await getToken()}` } });
      if (!r.ok) { const j = await r.json().catch(() => ({})); throw new Error(j.error || "Couldn't download it."); }
      const url = URL.createObjectURL(await r.blob());
      const a = document.createElement('a');
      a.href = url; a.download = d.fileName;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (e) { notify({ tone: 'error', title: "Couldn't download it", detail: e.message }); }
  }

  async function submit(e) {
    e.preventDefault();
    if (form.mode === 'upload' && !form.file) { setError('Choose the file to upload.'); return; }
    setBusy('save'); setError('');
    try {
      let path = form.path;
      if (form.mode === 'upload') {
        const f = form.file;
        const u = await apiCall(`${base}/upload-url`, getToken, { method: 'POST', body: JSON.stringify({ name: f.name, type: f.type, size: f.size }) });
        const res = await fetch(u.url, { method: 'PUT', headers: { 'Content-Type': f.type }, body: f });
        if (!res.ok) throw new Error(`The upload failed (${res.status}).`);
        path = u.path;
      }
      const { file, mode, ...fields } = form;   // eslint-disable-line no-unused-vars
      await apiCall(base, getToken, { method: 'POST', body: JSON.stringify({ ...fields, path }) });
      notify({ tone: 'success', title: form.mode === 'upload' ? 'Document uploaded' : 'Details saved', detail: path.split('/').pop() });
      setForm(null);
      await load();
    } catch (e2) { setError(e2.message); }
    setBusy('');
  }

  async function rename(e) {
    e.preventDefault();
    setBusy('rename'); setError('');
    try {
      const r = await apiCall(`${base}/rename`, getToken, { method: 'POST', body: JSON.stringify({ path: renaming.path, name: renaming.name }) });
      notify({ tone: 'success', title: 'Renamed', detail: `${r.fileName} (in the portal and in Cloud Storage)` });
      setRenaming(null);
      await load();
    } catch (e2) { setError(e2.message); }
    setBusy('');
  }

  async function remove(d) {
    const ok = await confirm({ title: `Delete ${d.fileName}?`, message: `It's removed from the portal and from ${account.name}'s folder in Google Cloud Storage.`, confirmLabel: 'Delete', danger: true });
    if (!ok) return;
    try {
      await apiCall(`${base}?path=${encodeURIComponent(d.path)}`, getToken, { method: 'DELETE' });
      notify({ tone: 'success', title: 'Document deleted', detail: d.fileName });
      await load();
    } catch (e) { notify({ tone: 'error', title: "Couldn't delete it", detail: e.message }); }
  }

  const describe = d => setForm({ mode: 'describe', path: d.path, fileName: d.fileName, type: d.type || 'service_agreement', label: d.label || '', signedOn: d.signedOn || '', expiresOn: d.expiresOn || '', notes: d.notes || '', replaces: '' });

  const row = d => (
    <tr key={d.path}>
      <td style={S.td}>
        <strong style={{ color: d.described ? 'var(--t-263238)' : 'var(--t-e65100)' }}>{d.typeLabel}</strong>
        {d.notes && <div style={S.small}>{d.notes}</div>}
      </td>
      <td style={S.td}>
        {renaming?.path === d.path ? (
          <form onSubmit={rename} style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <input value={renaming.name} onChange={e => setRenaming({ ...renaming, name: e.target.value })} style={{ ...S.input, minWidth: 220 }} aria-label="New file name" autoFocus />
            <button type="submit" disabled={busy === 'rename'} style={S.primaryBtn}>{busy === 'rename' ? 'Renaming…' : 'Save'}</button>
            <button type="button" onClick={() => setRenaming(null)} style={S.linkBtn}>Cancel</button>
          </form>
        ) : (
          <>
            <span style={{ wordBreak: 'break-word' }}>{d.fileName}</span>
            <div style={S.small}>{d.missing ? <span style={{ color: 'var(--t-c62828)' }}>Not in the folder any more (removed or renamed in Cloud Storage)</span> : kb(d.size || 0)}</div>
          </>
        )}
      </td>
      <td style={S.td}>{fmtDay(d.signedOn)}</td>
      <td style={{ ...S.td, ...(soon(d.expiresOn) && d.status !== 'replaced' ? { color: d.expiresOn < today() ? 'var(--t-c62828)' : 'var(--t-e65100)', fontWeight: 700 } : {}) }}>
        {fmtDay(d.expiresOn)}{d.expiresOn && d.expiresOn < today() && d.status !== 'replaced' ? ' · expired' : ''}
      </td>
      <td style={S.td}><span style={S.small}>{d.uploadedAt ? `${fmtDay(d.uploadedAt.slice(0, 10))}${d.uploadedBy ? ` · ${d.uploadedBy}` : ''}` : d.storedAt ? `${fmtDay(d.storedAt.slice(0, 10))} · added in Cloud Storage` : '—'}</span></td>
      <td style={{ ...S.td, whiteSpace: 'nowrap', textAlign: 'right' }}>
        {!d.missing && <>
          <button onClick={() => open(d, false)} style={S.actionBtn}>View</button>
          <button onClick={() => open(d, true)} style={S.actionBtn}>Download</button>
          <button onClick={() => setRenaming({ path: d.path, name: d.fileName })} style={S.actionBtn}>Rename</button>
          <button onClick={() => describe(d)} style={d.described ? S.actionBtn : { ...S.actionBtn, ...S.actionStrong }}>{d.described ? 'Edit details' : 'Describe'}</button>
          <button onClick={() => remove(d)} style={{ ...S.actionBtn, ...S.actionDanger }}>Delete</button>
        </>}
      </td>
    </tr>
  );

  return (
    <div style={S.box} onClick={e => e.stopPropagation()}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div>
          <strong style={{ color: 'var(--t-00695c)', fontSize: 15 }}><Emo e="📄" /> Documents</strong>
          <div style={S.small}>Signed contracts and other papers for {account.name}. Super Admins only.{data?.folder && <> Folder in Cloud Storage: <code>{data.bucket}/{data.folder}</code></>}</div>
        </div>
        {!form && <button onClick={() => { setForm({ mode: 'upload', ...EMPTY, file: null }); setError(''); }} style={S.primaryBtn}><Emo e="⬆" /> Upload document</button>}
      </div>

      {error && <div role="alert" style={S.error}>{error}</div>}

      {form && (
        <form onSubmit={submit} style={S.form}>
          <strong style={{ fontSize: 13, color: 'var(--t-00695c)' }}>{form.mode === 'upload' ? 'Upload a document' : `Details for ${form.fileName}`}</strong>
          {form.mode === 'upload' && (
            <label style={S.label}>File <span style={S.hint}>(PDF, Word, PNG or JPG, up to 25 MB; it keeps its name)</span>
              <input type="file" accept={ACCEPT} onChange={e => setForm({ ...form, file: e.target.files?.[0] || null })} style={S.input} />
            </label>
          )}
          <div style={S.grid}>
            <label style={S.label}>Kind
              <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })} style={S.input}>
                {Object.entries(types).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </label>
            {form.type === 'other' && (
              <label style={S.label}>Name of the document
                <input value={form.label} onChange={e => setForm({ ...form, label: e.target.value })} placeholder="e.g. Pricing addendum" maxLength={80} style={S.input} />
              </label>
            )}
            <label style={S.label}>Signed on
              <input type="date" value={form.signedOn} onChange={e => setForm({ ...form, signedOn: e.target.value })} style={S.input} />
            </label>
            <label style={S.label}>Renews or expires on <span style={S.hint}>(optional; reminder 30 days before)</span>
              <input type="date" value={form.expiresOn} onChange={e => setForm({ ...form, expiresOn: e.target.value })} style={S.input} />
            </label>
          </div>
          {current.some(d => d.described && d.path !== form.path) && (
            <label style={S.label}>Replaces <span style={S.hint}>(optional: the older one moves to Earlier versions; nothing is deleted)</span>
              <select value={form.replaces} onChange={e => setForm({ ...form, replaces: e.target.value })} style={S.input}>
                <option value="">Nothing, it's a new document</option>
                {current.filter(d => d.described && d.path !== form.path).map(d => <option key={d.path} value={d.path}>{d.typeLabel}: {d.fileName}</option>)}
              </select>
            </label>
          )}
          <label style={S.label}>Notes <span style={S.hint}>(optional)</span>
            <input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} maxLength={500} style={S.input} />
          </label>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="submit" disabled={busy === 'save'} style={S.primaryBtn}>{busy === 'save' ? 'Saving…' : form.mode === 'upload' ? 'Upload' : 'Save details'}</button>
            <button type="button" onClick={() => { setForm(null); setError(''); }} style={S.cancelBtn}>Cancel</button>
          </div>
        </form>
      )}

      {!data ? (!error && <Loader size="sm" />) : !docs.length ? (
        <p style={S.small}>No documents yet. Upload one here, or put files straight into the client's folder in Cloud Storage: they show up here.</p>
      ) : (
        <>
          <div style={{ overflowX: 'auto' }}>
            <table style={S.table}>
              <thead><tr>{['Document', 'File', 'Signed', 'Renews', 'Added', ''].map(h => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
              <tbody>{current.map(row)}</tbody>
            </table>
          </div>
          {older.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <button onClick={() => setShowOld(v => !v)} style={{ ...PILL, padding: '4px 12px', fontSize: 12 }}>{showOld ? '▲' : '▼'} Earlier versions ({older.length})</button>
              {showOld && <div style={{ overflowX: 'auto', marginTop: 6 }}><table style={S.table}><tbody>{older.map(row)}</tbody></table></div>}
            </div>
          )}
        </>
      )}
      {viewing && <DocumentViewer doc={viewing} fileUrl={fileUrl(viewing)} onClose={() => setViewing(null)} />}
    </div>
  );
}

const S = {
  box: { marginTop: 14, padding: '12px 14px', border: '1px solid var(--c-e0f2f1)', borderRadius: 10, background: 'var(--c-ffffff)', cursor: 'default' },
  small: { fontSize: 12, color: 'var(--t-78909c)', lineHeight: 1.5 },
  hint: { fontWeight: 400, color: 'var(--t-90a4ae)', textTransform: 'none', letterSpacing: 0 },
  error: { marginTop: 10, background: 'var(--c-ffebee)', color: 'var(--t-c62828)', padding: '8px 12px', borderRadius: 8, fontSize: 13 },
  form: { marginTop: 10, padding: 12, background: 'var(--c-f7faf9)', border: '1px solid var(--c-e0f2f1)', borderRadius: 10, display: 'grid', gap: 10 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10 },
  label: { display: 'grid', gap: 4, fontSize: 12, fontWeight: 700, color: 'var(--t-546e7a)' },
  input: { padding: '7px 10px', borderRadius: 8, border: '1.5px solid var(--c-cfd8dc)', fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box' },
  table: { width: '100%', borderCollapse: 'collapse', marginTop: 10, fontSize: 13 },
  th: { textAlign: 'left', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--t-78909c)', padding: '6px 8px', borderBottom: '1px solid var(--c-e0f2f1)' },
  td: { padding: '8px', borderBottom: '1px solid var(--c-f0f4f3)', verticalAlign: 'top' },
  primaryBtn: { padding: '7px 14px', background: 'var(--c-00897b)', color: 'var(--t-ffffff)', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 700, fontFamily: 'inherit' },
  cancelBtn: { padding: '7px 14px', background: 'var(--c-ffffff)', border: '1.5px solid var(--c-cfd8dc)', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, color: 'var(--t-455a64)', fontFamily: 'inherit' },
  actionBtn: { padding: '3px 9px', marginLeft: 4, background: 'var(--c-ffffff)', border: '1px solid var(--c-b2dfdb)', color: 'var(--t-00695c)', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: 'inherit' },
  actionDanger: { borderColor: 'var(--c-ef9a9a)', color: 'var(--t-c62828)' },
  actionStrong: { background: 'var(--c-fff3e0)', borderColor: 'var(--c-ffb74d)', color: 'var(--t-e65100)' },
  linkBtn: { background: 'none', border: 'none', color: 'var(--t-00695c)', cursor: 'pointer', fontSize: 12, textDecoration: 'underline', fontFamily: 'inherit' },
};
